import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface MarketingSessionData {
  fbp?: string;
  fbc?: string;
  fbclid?: string;
  gclid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrer?: string;
  landingPage?: string;
  sessionId: string;
}

const MARKETING_SESSION_COOKIE = '_jt_mkt_session';
const COOKIE_MAX_AGE_DAYS = 30;

@Injectable({
  providedIn: 'root',
})
export class MarketingSessionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/marketing-sessions`;

  /**
   * Initialize marketing session tracking.
   * Should be called on app initialization.
   * Non-blocking - does not wait for FB cookies to avoid delaying app load.
   */
  initialize(): void {
    // Skip if running on server (SSR)
    if (typeof window === 'undefined') {
      return;
    }

    // Check if we already have a marketing session
    const existingSessionId = this.getSessionIdFromCookie();
    if (existingSessionId) {
      console.log('[MarketingSession] Session already exists:', existingSessionId);
      return;
    }

    // Capture UTM parameters and other data from URL
    const urlParams = this.getUrlParams();

    // Check if we have any marketing data worth tracking
    if (!this.hasMarketingData(urlParams)) {
      console.log('[MarketingSession] No marketing data in URL, skipping');
      return;
    }

    // Generate session ID immediately
    const sessionId = this.generateSessionId();

    // Store session ID in cookie immediately
    this.setSessionCookie(sessionId);

    // Capture referrer and landing page now (before any navigation)
    const referrer = document.referrer || undefined;
    const landingPage = window.location.href;

    // Wait for FB cookies asynchronously (non-blocking)
    this.waitForFbCookiesAndSend(sessionId, urlParams, referrer, landingPage);
  }

  /**
   * Wait for FB cookies and send data to backend (non-blocking)
   */
  private async waitForFbCookiesAndSend(
    sessionId: string,
    urlParams: Partial<MarketingSessionData>,
    referrer: string | undefined,
    landingPage: string,
  ): Promise<void> {
    // Wait for Facebook Pixel to set cookies (with retry)
    const fbCookies = await this.waitForFbCookies();

    // Build session data
    const sessionData: MarketingSessionData = {
      sessionId,
      ...urlParams,
      ...fbCookies,
      referrer,
      landingPage,
    };

    // Send to backend
    this.sendToBackend(sessionData);

    console.log('[MarketingSession] New session created:', sessionData);
  }

  /**
   * Get UTM parameters and click IDs from URL
   */
  private getUrlParams(): Partial<MarketingSessionData> {
    const params = new URLSearchParams(window.location.search);

    return {
      utmSource: params.get('utm_source') || undefined,
      utmMedium: params.get('utm_medium') || undefined,
      utmCampaign: params.get('utm_campaign') || undefined,
      utmTerm: params.get('utm_term') || undefined,
      utmContent: params.get('utm_content') || undefined,
      fbclid: params.get('fbclid') || undefined,
      gclid: params.get('gclid') || undefined,
    };
  }

  /**
   * Check if we have any marketing-related data
   */
  private hasMarketingData(params: Partial<MarketingSessionData>): boolean {
    return !!params.utmCampaign;
  }

  /**
   * Wait for Facebook Pixel cookies to be set.
   * Retries up to 5 times with 500ms delay.
   */
  private async waitForFbCookies(
    maxRetries = 10,
    delayMs = 500,
  ): Promise<{ fbp?: string; fbc?: string }> {
    for (let i = 0; i < maxRetries; i++) {
      const fbp = this.getCookie('_fbp');
      const fbc = this.getCookie('_fbc');

      if (fbp) {
        return { fbp, fbc: fbc || undefined };
      }

      // Wait before retrying
      await this.delay(delayMs);
    }

    // Return whatever we have after all retries
    return {
      fbp: this.getCookie('_fbp') || undefined,
      fbc: this.getCookie('_fbc') || undefined,
    };
  }

  /**
   * Get session ID from cookie
   */
  private getSessionIdFromCookie(): string | null {
    return this.getCookie(MARKETING_SESSION_COOKIE);
  }

  /**
   * Set session cookie with 30-day expiration
   */
  private setSessionCookie(sessionId: string): void {
    const maxAge = COOKIE_MAX_AGE_DAYS * 24 * 60 * 60; // Convert days to seconds
    document.cookie = `${MARKETING_SESSION_COOKIE}=${sessionId}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `mkt_${timestamp}_${random}`;
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  }

  /**
   * Send session data to backend
   */
  private sendToBackend(data: MarketingSessionData): void {
    this.http.post(this.apiUrl, data).subscribe({
      next: () => {
        console.log('[MarketingSession] Session saved to backend');
      },
      error: (err) => {
        console.error('[MarketingSession] Failed to save session:', err);
      },
    });
  }

  /**
   * Utility delay function
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Get current session ID (useful for linking to user on signup)
   */
  getSessionId(): string | null {
    return this.getSessionIdFromCookie();
  }

  /**
   * Clear the marketing session cookie.
   * Should be called on user logout.
   */
  clearSession(): void {
    if (typeof document === 'undefined') {
      return;
    }
    // Delete cookie by setting max-age to 0
    document.cookie = `${MARKETING_SESSION_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
    console.log('[MarketingSession] Session cleared');
  }

  /**
   * Link current marketing session to the authenticated user.
   * Should be called after successful authentication.
   */
  linkToCurrentUser(): void {
    const sessionId = this.getSessionIdFromCookie();

    if (!sessionId) {
      console.log('[MarketingSession] No session to link');
      return;
    }

    this.http.post(`${this.apiUrl}/link`, { sessionId }).subscribe({
      next: () => {
        console.log('[MarketingSession] Session linked to user');
      },
      error: (err) => {
        console.error('[MarketingSession] Failed to link session:', err);
      },
    });
  }

  /**
   * Track an event for the current user's marketing session.
   * Creates a session if none exists.
   */
  trackEvent(event: string, params?: Record<string, any>): void {
    const sessionId = this.getSessionIdFromCookie();
    const fbp = this.getCookie('_fbp') || undefined;
    const fbc = this.getCookie('_fbc') || undefined;
    const currentUrl = typeof window !== 'undefined' ? window.location.href : undefined;

    const payload = {
      event,
      params,
      sessionId: sessionId || undefined,
      fbp,
      fbc,
      currentUrl,
    };

    this.http.post(`${this.apiUrl}/track-event`, payload).subscribe({
      next: () => {
        console.log('[MarketingSession] Event tracked:', event, params);
      },
      error: (err) => {
        console.error('[MarketingSession] Failed to track event:', err);
      },
    });
  }
}
